/**
 * Service for fetching agent workflow URL and authentication keys.
 * Based on the LogicAppsUX implementation for A2A chat functionality.
 */

import { isLocalMode, getLocalRuntimeUrl, localFetch } from './localRuntimeClient';
import { getArmBaseUrl } from './armConfig';
import { buildAcaArmUrl, buildRuntimeUrl, buildVfsUrl } from './acaAppUrl';

const API_VERSION = '2018-11-01';
const API_KEY_EXPIRY_MS = 86400000; // 24 hours in milliseconds

export interface AgentUrlConfig {
  agentUrl: string;
  chatUrl: string;
  apiKey?: string | undefined;
  oboUserToken?: string | undefined;
  hostName: string;
  authenticationEnabled?: boolean | undefined;
}

export interface AgentUrlFetchOptions {
  subscriptionId: string;
  resourceGroup: string;
  appName: string;
  workflowName: string;
  accessToken: string;
  hostName: string;
  isDraftMode?: boolean | undefined;
}

const buildArmUrl = (opts: AgentUrlFetchOptions): string =>
  buildAcaArmUrl(opts.subscriptionId, opts.resourceGroup, opts.appName);

/**
 * Fetch authentication settings to check if EasyAuth is enabled
 */
const fetchAuthentication = async (
  armResourceUrl: string,
  accessToken: string
): Promise<{ enabled: boolean } | null> => {
  try {
    const url = `${armResourceUrl}/authConfigs/current?api-version=2024-03-01`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return { enabled: data?.properties?.platform?.enabled ?? false };
  } catch (error) {
    console.error('Failed to fetch authentication settings:', error);
    return null;
  }
};

/**
 * Fetch A2A authentication key for the workflow.
 * In local mode, calls the local runtime directly.
 */
const fetchA2AAuthKey = async (
  _armResourceUrl: string,
  workflowName: string,
  accessToken: string,
  isDraftMode?: boolean,
  hostName?: string
): Promise<{ key?: string; endpoint?: string } | null> => {
  try {
    const currentDate = new Date();
    const endpoint = isDraftMode ? 'listDraftApiKeys' : 'listApiKeys';
    
    // In local mode, call the local runtime directly
    if (isLocalMode()) {
      const localUrl = `${getLocalRuntimeUrl()}/runtime/webhooks/workflow/api/management/workflows/${workflowName}/${endpoint}?api-version=${API_VERSION}`;
      return await localFetch<{ key?: string; endpoint?: string }>(localUrl, {
        method: 'POST',
        body: {
          expiry: new Date(currentDate.getTime() + API_KEY_EXPIRY_MS).toISOString(),
          keyType: 'Primary',
        },
      });
    }

    const url = `${buildRuntimeUrl(hostName ?? '')}/workflows/${workflowName}/${endpoint}?api-version=${API_VERSION}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        expiry: new Date(currentDate.getTime() + API_KEY_EXPIRY_MS).toISOString(),
        keyType: 'Primary',
      }),
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  } catch (error) {
    console.error('Failed to fetch A2A auth key:', error);
    return null;
  }
};

/**
 * Fetch OBO (On-Behalf-Of) data for dynamic connections
 */
const fetchOBOData = async (
  _armResourceUrl: string,
  accessToken: string,
  hostName?: string
): Promise<{ key?: string } | null> => {
  try {
    // Get connections configuration via VFS
    const connectionsUrl = `${buildVfsUrl(hostName ?? '')}/connections.json`;
    const connectionsResponse = await fetch(connectionsUrl, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!connectionsResponse.ok) {
      return null;
    }

    const connections = await connectionsResponse.json();

    // Find dynamic connection
    const apiConnections = connections?.managedApiConnections ?? {};
    let connectionId = '';

    for (const key of Object.keys(apiConnections)) {
      const conn = apiConnections[key];
      if (conn?.runtimeSource?.toLowerCase() === 'dynamic') {
        connectionId = conn?.connection?.id;
        break;
      }
    }

    if (!connectionId) {
      return null;
    }

    // Get OBO key for the dynamic connection
    const oboUrl = `${getArmBaseUrl()}${connectionId}/listDynamicConnectionKeys?api-version=2015-08-01-preview`;
    const oboResponse = await fetch(oboUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!oboResponse.ok) {
      return null;
    }

    const oboData = await oboResponse.json();
    return { key: oboData?.properties?.key ?? oboData?.key };
  } catch (error) {
    console.error('Failed to fetch OBO data:', error);
    return null;
  }
};

/**
 * Fetches the agent URL configuration including API key and OBO token.
 * This is used to connect to agentic workflows via the A2A protocol.
 * In local mode, uses the local runtime URL and skips ARM-specific calls.
 */
export const fetchAgentUrlConfig = async (
  opts: AgentUrlFetchOptions
): Promise<AgentUrlConfig> => {
  const { workflowName, hostName, isDraftMode, accessToken } = opts;

  if (!workflowName || !hostName) {
    return { agentUrl: '', chatUrl: '', hostName: '' };
  }

  // Handle local mode
  if (isLocalMode()) {
    const localUrl = getLocalRuntimeUrl();
    
    try {
      // Get A2A authentication key from local runtime
      const a2aData = await fetchA2AAuthKey('', workflowName, accessToken, isDraftMode);
      
      // Build local URLs - use relative path to go through Vite proxy and avoid CORS issues
      // The proxy is configured in vite.config.ts to forward /api/* to the local runtime
      const agentUrl = `/api/agents/${workflowName}/v1`;
      const chatUrl = `/api/agentsChat/${workflowName}`;
      
      return {
        agentUrl,
        chatUrl,
        apiKey: a2aData?.key,
        oboUserToken: undefined, // Not needed for local
        hostName: localUrl.replace(/^https?:\/\//, ''),
        authenticationEnabled: false,
      };
    } catch (error) {
      console.error('Failed to fetch local agent URL config:', error);
      return { agentUrl: '', chatUrl: '', hostName: localUrl.replace(/^https?:\/\//, '') };
    }
  }

  const armResourceUrl = buildArmUrl(opts);

  try {
    let apiKey: string | undefined;
    let oboUserToken: string | undefined;
    let a2aCodeForDraft = '';

    // Check if authentication is enabled
    const authentication = await fetchAuthentication(armResourceUrl, accessToken);
    const authenticationEnabled = authentication?.enabled ?? false;

    if (!authenticationEnabled) {
      // Get A2A authentication key
      const a2aData = await fetchA2AAuthKey(armResourceUrl, workflowName, accessToken, isDraftMode, hostName);

      if (isDraftMode && a2aData?.endpoint) {
        const endpointParts = a2aData.endpoint.split('?');
        a2aCodeForDraft = endpointParts.pop() ?? '';
      }

      // Get OBO data if available
      const oboData = await fetchOBOData(armResourceUrl, accessToken, hostName);

      if (a2aData?.key) {
        apiKey = a2aData.key;

        if (oboData?.key) {
          oboUserToken = oboData.key;
        }
      }
    }

    // Build URLs
    const agentBaseUrl = hostName.startsWith('https://') ? hostName : `https://${hostName}`;
    const agentUrl = `${agentBaseUrl}/api/Agents/${workflowName}`;

    const agentCardUrlForDraft = `${agentBaseUrl}/runtime/webhooks/workflow/scaleUnits/prod-00/agents/${workflowName}/draft/.well-known/agent-card.json${
      a2aCodeForDraft ? `${encodeURIComponent(`?${a2aCodeForDraft}`)}` : ''
    }`;

    const chatUrl = `${agentBaseUrl}/api/agentsChat/${workflowName}/IFrame${
      isDraftMode ? `?agentCard=${agentCardUrlForDraft}` : ''
    }`;

    return {
      agentUrl,
      chatUrl,
      apiKey,
      oboUserToken,
      hostName,
      authenticationEnabled,
    };
  } catch (error) {
    console.error('Failed to fetch agent URL config:', error);
    return { agentUrl: '', chatUrl: '', hostName };
  }
};

/**
 * Checks if a workflow is an agentic workflow based on its kind.
 * Agentic workflows have kind 'Stateful' with specific agent triggers/actions.
 */
export const isAgentWorkflow = (workflowKind?: string): boolean => {
  // In the LogicAppsUX codebase, this checks for specific workflow kinds
  // For now, we check if the kind indicates an agent workflow
  return workflowKind?.toLowerCase() === 'agent' || workflowKind?.toLowerCase() === 'stateful-agent';
};
